import { useState, useEffect } from 'react';
import { ShieldCheck, ShieldAlert, Sparkles, UserPlus, Link, Plus, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { qualificarCnae, labelCnae, temSecundarioAprovado } from '../utils/cnae';
import { useToast } from '../contexts/ToastContext';
import { formatCnpj, formatCep } from '../utils/masks';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export default function ReceitaFederalTab({ conta, onEnriched, onCreateConta }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contactsAdded, setContactsAdded] = useState({}); // { [nome]: boolean }
  const [filiaisCrm, setFiliaisCrm] = useState({}); // { [cnpj]: contaObj }
  const [linkingFilial, setLinkingFilial] = useState({}); // { [cnpj]: boolean }

  const dataEnrich = conta.cnpj_enriched_at;
  const isEnriched = !!dataEnrich;

  // Safe parsing helper
  const safeJsonParse = (str, fallback = []) => {
    if (!str) return fallback;
    try {
      return typeof str === 'string' ? JSON.parse(str) : str;
    } catch (e) {
      console.error('[RF Tab] Parse error:', e);
      return fallback;
    }
  };

  const cnaesSecundarios = safeJsonParse(conta.cnaes_secundarios, []);
  const filiaisReceita = safeJsonParse(conta.filiais_receita, []);
  const qsa = safeJsonParse(conta.qsa_data, []);

  // Check which branch CNPJs exist in CRM
  useEffect(() => {
    if (filiaisReceita.length > 0) {
      filiaisReceita.forEach(f => {
        const cleanCnpj = f.cnpj.replace(/\D/g, '');
        api.getContas({ search: cleanCnpj, autocomplete: '1' })
          .then(res => {
            const rows = res?.data || res || [];
            const match = rows.find(c => c.cnpj.replace(/\D/g, '') === cleanCnpj);
            if (match) {
              setFiliaisCrm(prev => ({ ...prev, [cleanCnpj]: match }));
            }
          })
          .catch(() => {});
      });
    }
    // eslint-disable-next-line
  }, [conta.id, conta.filiais_receita]);

  const getDaysSince = (dateStr) => {
    if (!dateStr) return 'Nunca';
    // SQLite datetime('now') stores UTC without timezone marker (e.g. "2026-06-01 21:30:00").
    // Without the 'Z' suffix, JS parses it as local time — in Brazil (UTC-3) that shifts
    // the timestamp 3h into the future, producing a negative diff. Force UTC parsing.
    const utcStr = (dateStr.includes('Z') || dateStr.includes('+'))
      ? dateStr
      : dateStr.replace(' ', 'T') + 'Z';
    const days = Math.floor((Date.now() - new Date(utcStr).getTime()) / 86400000);
    if (days <= 0) return 'hoje';
    return days === 1 ? '1 dia' : `${days} dias`;
  };

  async function handleEnrich() {
    setLoading(true);
    setError('');
    try {
      const res = await api.enrichConta(conta.id);
      if (res.ok && res.data) {
        onEnriched(res.data);
      } else {
        throw new Error(res.error || 'Falha ao enriquecer conta');
      }
    } catch (err) {
      setError(err.message || 'Erro ao consultar Receita Federal');
    } finally {
      setLoading(false);
    }
  }

  async function handlePromoverSocio(socio) {
    try {
      await api.createContatoConta({
        conta_id: conta.id,
        nome: socio.nome,
        cargo: socio.qualificacao || 'Sócio',
      });
      setContactsAdded(prev => ({ ...prev, [socio.nome]: true }));
    } catch (err) {
      toast.error('Erro ao cadastrar contato: ' + err.message);
    }
  }

  async function handleVincularFilial(filialCrmObj) {
    const cleanCnpj = filialCrmObj.cnpj.replace(/\D/g, '');
    setLinkingFilial(prev => ({ ...prev, [cleanCnpj]: true }));
    try {
      await api.updateConta(filialCrmObj.id, {
        ...filialCrmObj,
        parent_conta_id: conta.id
      });
      // Refresh the branch locally
      setFiliaisCrm(prev => ({
        ...prev,
        [cleanCnpj]: { ...prev[cleanCnpj], parent_conta_id: conta.id }
      }));
    } catch (err) {
      toast.error('Erro ao vincular filial: ' + err.message);
    } finally {
      setLinkingFilial(prev => ({ ...prev, [cleanCnpj]: false }));
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-[#00DBFF] border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-gray-500 font-medium">Buscando dados na Receita Federal...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-md mx-auto space-y-4">
        <p className="text-sm text-red-700 font-medium">{error}</p>
        <button
          type="button"
          onClick={handleEnrich}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!isEnriched) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
        <Sparkles size={36} className="text-[#00DBFF] mb-3 animate-pulse" />
        <h4 className="text-sm font-semibold text-gray-700 mb-1">Nenhum dado da Receita Federal ainda</h4>
        <p className="text-xs text-gray-400 mb-4 max-w-sm text-center">
          Consulte e enriqueça esta conta com a situação cadastral, CNAEs secundários, QSA completo e filiais da Receita.
        </p>
        <button
          type="button"
          onClick={handleEnrich}
          className="px-4 py-2 bg-[#00DBFF] hover:bg-[#00DBFF]/80 text-[#002233] font-bold text-xs rounded-lg transition-all shadow-[0_0_12px_rgba(0,219,255,0.2)]"
        >
          Consultar Receita Federal
        </button>
      </div>
    );
  }

  const sitStatus = conta.situacao_cadastral || conta.situacao;
  // Comparação case-insensitive: a publica.cnpj.ws devolve "Ativa" e a BrasilAPI
  // "ATIVA". O `uppercase` do CSS mascara a diferença, então um `=== 'ATIVA'`
  // estrito pintava a situação de vermelho mesmo com a empresa ativa.
  const isAtiva = (sitStatus || '').trim().toUpperCase() === 'ATIVA';
  const cnaePrincipalStatus = qualificarCnae(conta.cnae_principal_codigo);
  const principalAprovado = cnaePrincipalStatus === 'aprovado';
  const secundarioAprovado = temSecundarioAprovado(cnaesSecundarios);

  return (
    <div className="space-y-6">
      {/* ── HEADER ── */}
      <div className="flex items-center justify-between border-b pb-3">
        <span className="text-xs text-gray-500 font-medium">
          Atualizado há <strong className="text-gray-700">{getDaysSince(dataEnrich)}</strong> ({new Date(dataEnrich).toLocaleDateString('pt-BR')})
        </span>
        <button
          type="button"
          onClick={handleEnrich}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 hover:border-[#00DBFF] hover:bg-gray-50 rounded-lg text-xs font-semibold text-gray-600 hover:text-[#002233] transition-all cursor-pointer"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Atualizar ficha
        </button>
      </div>

      {/* ── SITUAÇÃO CADASTRAL ── */}
      <div className={`p-4 rounded-xl border flex items-start gap-3.5 shadow-sm transition-all duration-300 ${
        isAtiva
          ? 'bg-emerald-50/50 border-emerald-200 text-emerald-800'
          : 'bg-rose-50/50 border-rose-200 text-rose-800'
      }`}>
        {isAtiva ? (
          <ShieldCheck size={24} className="text-emerald-500 shrink-0 mt-0.5" />
        ) : (
          <ShieldAlert size={24} className="text-rose-500 shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-sm font-bold uppercase tracking-wider">
              SITUAÇÃO: {sitStatus || 'NÃO CONSTA'}
            </span>
            {conta.motivo_situacao && (
              <span className="text-xs opacity-75">• {conta.motivo_situacao}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {conta.simples_nacional === 1 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 shadow-sm">
                Simples Nacional
                {conta.simples_data_opcao && (
                  <span className="font-normal ml-1">({conta.simples_data_opcao.slice(0, 4)})</span>
                )}
              </span>
            )}
            {conta.porte && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                Porte: {conta.porte}
              </span>
            )}
            {conta.capital_social > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                Capital: {fmtBRL(conta.capital_social)}
              </span>
            )}
            {conta.data_abertura && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 border border-gray-200">
                Abertura: {conta.data_abertura}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── CNAE + ENDEREÇO (grid 2 colunas) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CNAEs */}
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Atividade Principal</h4>
            <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
              <div className="flex items-start gap-2">
                <span className={`text-[10px] font-bold shrink-0 px-2 py-0.5 rounded-full border ${
                  principalAprovado
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  {labelCnae(cnaePrincipalStatus)}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-gray-500 font-mono">{conta.cnae_principal_codigo}</p>
                  <p className="text-sm text-gray-700 font-medium mt-0.5">{conta.cnae_principal_descricao || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Atividades Secundárias ({cnaesSecundarios.length})</h4>
            {cnaesSecundarios.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Nenhum CNAE secundário declarado.</p>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
                {cnaesSecundarios.map((c, i) => {
                  const status = qualificarCnae(c.codigo);
                  const isApp = status === 'aprovado';
                  return (
                    <div
                      key={i}
                      title={c.descricao}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border cursor-help ${
                        isApp
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600'
                      }`}
                    >
                      <span className="font-mono font-bold text-[10px]">{c.codigo}</span>
                      <span className="truncate max-w-[200px]">{c.descricao}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {!principalAprovado && secundarioAprovado && (
              <p className="text-xs text-green-600 italic font-medium mt-2">
                * Nota: Esta empresa possui CNAE secundário pré-aprovado para Totvs Moda.
              </p>
            )}
          </div>
        </div>

        {/* Endereço */}
        <div>
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Endereço da Receita</h4>
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2.5 text-sm text-gray-700">
            <div>
              <span className="text-xs text-gray-400 font-medium block">Logradouro</span>
              <p className="font-medium">{conta.endereco_rua || '—'}{conta.endereco_numero ? `, nº ${conta.endereco_numero}` : ''}</p>
            </div>
            {conta.endereco_bairro && (
              <div>
                <span className="text-xs text-gray-400 font-medium block">Bairro</span>
                <p className="font-medium">{conta.endereco_bairro}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-xs text-gray-400 font-medium block">Cidade/UF</span>
                <p className="font-medium">{conta.cidade || '—'}{conta.estado ? `/${conta.estado}` : ''}</p>
              </div>
              <div>
                <span className="text-xs text-gray-400 font-medium block">CEP</span>
                <p className="font-medium font-mono">{conta.endereco_cep ? formatCep(conta.endereco_cep) : '—'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── QSA — QUADRO SOCIETÁRIO ── */}
      <div>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
          Quadro de Sócios e Administradores (QSA)
          <span className="bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 text-[10px]">{qsa.length}</span>
        </h4>
        {qsa.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Quadro societário não disponível.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="divide-y divide-gray-100">
              {qsa.map((s, idx) => {
                const isPJ = s.tipo === 'PJ';
                const isAdded = !!contactsAdded[s.nome];
                return (
                  <div key={idx} className={`flex items-center justify-between p-3 gap-4 text-sm transition-colors ${
                    isPJ ? 'opacity-55 hover:bg-gray-50/30' : 'hover:bg-gray-50/50'
                  }`}>
                    <div className="min-w-0" title={isPJ ? 'Pessoa Jurídica — não aplicável para contatos' : ''}>
                      <p className="font-semibold text-gray-800 truncate">{s.nome}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{s.qualificacao || 'Sócio'}</p>
                    </div>
                    {isPJ ? (
                      <span className="text-[10px] bg-gray-100 border border-gray-200 text-gray-400 rounded-md px-2 py-1 uppercase tracking-wide shrink-0">
                        PJ
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handlePromoverSocio(s)}
                        disabled={isAdded}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide border cursor-pointer transition-all shrink-0 ${
                          isAdded
                            ? 'bg-emerald-50 border-emerald-100 text-emerald-600 font-medium'
                            : 'bg-white hover:bg-blue-50 border-gray-300 hover:border-blue-400 text-gray-600 hover:text-blue-700 shadow-sm'
                        }`}
                      >
                        <UserPlus size={12} />
                        {isAdded ? '✓ Promovido' : 'Promover a Contato'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── FILIAIS DA RECEITA ── */}
      <div>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
          Filiais Registradas na Receita Federal
          <span className="bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 text-[10px]">{filiaisReceita.length}</span>
        </h4>
        {filiaisReceita.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Nenhuma filial informada pela Receita Federal.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filiaisReceita.map((f, idx) => {
              const cleanCnpj = f.cnpj.replace(/\D/g, '');
              const crmMatch = filiaisCrm[cleanCnpj];
              const isLinked = crmMatch?.parent_conta_id === conta.id;
              const isLinking = !!linkingFilial[cleanCnpj];

              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border transition-all duration-300 flex flex-col justify-between gap-3 ${
                    crmMatch
                      ? isLinked
                        ? 'bg-emerald-50/40 border-emerald-200'
                        : 'bg-sky-50/50 border-sky-200'
                      : 'bg-white border-gray-200 hover:shadow-sm'
                  }`}
                >
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-mono text-xs text-gray-600 font-semibold">{formatCnpj(f.cnpj)}</span>
                      {f.situacao && (
                        <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                          f.situacao === 'ATIVA'
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-red-100 text-red-700 border-red-200'
                        }`}>
                          {f.situacao}
                        </span>
                      )}
                    </div>
                    {f.cidade && (
                      <p className="text-xs text-gray-500 mt-1">{[f.cidade, f.uf].filter(Boolean).join('/')}</p>
                    )}

                    {crmMatch && (
                      <div className="mt-2 text-xs">
                        <p className={`font-semibold ${isLinked ? 'text-emerald-700' : 'text-sky-800'}`}>
                          {isLinked ? '✓ Filial vinculada' : '✓ Cadastrada no CRM'}
                        </p>
                        <p className="text-gray-500 text-[11px] truncate mt-0.5">Nome: {crmMatch.nome}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-1">
                    {crmMatch ? (
                      !isLinked && (
                        <button
                          type="button"
                          onClick={() => handleVincularFilial(crmMatch)}
                          disabled={isLinking}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00DBFF] hover:bg-[#00DBFF]/80 text-[#002233] font-bold text-xs rounded-lg transition-colors cursor-pointer"
                        >
                          <Link size={12} />
                          {isLinking ? 'Vinculando...' : 'Vincular como Filial'}
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => onCreateConta?.({ cnpj: f.cnpj, parent_conta_id: conta.id })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-800 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                      >
                        <Plus size={12} />
                        Criar Conta
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
